'use client';
import { useState, useEffect } from 'react';
import MaybeLink from '@/components/apt/MaybeLink';
import { priceChangeCompact, priceChangeDirection } from '@/lib/apt/price-change';
import { fmt } from '@/lib/fmt';
import { cellOrNull, visibleColumns } from '@/components/ui/NullCell';
import { fmtCnt } from '@/lib/apt-format';

// 「변동률」 열은 2026-08-26 에 한 번 «뺐다가» 되살렸다.
//   뺀 이유: `price_change_1y` 는 대표 평형 하나를 고정해 잰 값인데, 평형을 안 붙이면
//   「단지 전체가 그만큼 움직였다」로 읽힌다 (실측 부호 반전 23.9%). 그때 이 표가 쓰는 RPC
//   `get_nearby_apt_compare` 가 근거 컬럼을 돌려주지 않아 붙일 수가 없었다.
//   되살린 이유: RPC 가 `price_change_area` · `n_recent` · `n_past` 를 «기존 컬럼 뒤에» 추가했다.
//
// ⚠️ 표시는 반드시 `priceChangeCompact()` 를 거친다 — 평형이 빠진 % 를 직접 조립하지 말 것.
//    근거가 하나라도 없으면 그 칸은 빈 칸(sr-only 「해당 없음」)이다 (lib/apt/price-change.ts · ㉑ §3-7).
export interface Apt {
  apt_name: string; latest_sale_price: number; avg_sale_price_pyeong: number;
  jeonse_ratio: number; sale_count_1y: number; built_year: number;
  price_change_1y: number | null; price_change_area: number | null;
  price_change_n_recent: number | null; price_change_n_past: number | null;
  /** ⑲ⓒ — 단지백과 링크(색인 대상일 때만). 서버(`/api/public/apt-nearby`)가 채운다. */
  complex_href?: string | null;
}

export default function AptNearbyCompare({ aptName, sigungu, region }: { aptName: string; sigungu: string; region?: string | null }) {
  const [data, setData] = useState<Apt[]>([]);
  useEffect(() => {
    /* ⚠️ region 없이 부르지 않는다 — 시군구 이름이 전국에서 유일하지 않아
     *    부산 북구 단지의 주변에 대구 북구 단지가 섞인다(라우트 주석 참조).
     *    라우트가 400 을 내지만 요청 자체를 안 보내는 게 맞다. */
    if (!region) return;
    const qs = new URLSearchParams({ apt: aptName, sigungu, region });
    fetch(`/api/public/apt-nearby?${qs}`)
      .then(r => r.ok ? r.json() : null).then(d => setData(d?.data || [])).catch(() => {});
  }, [aptName, sigungu, region]);

  return <NearbyCompareTable data={data} sigungu={sigungu} />;
}

/** 표 본체 — 데이터 조회와 분리해 서버 렌더 테스트(G-null)에서 그대로 그린다. */
export function NearbyCompareTable({ data, sigungu }: { data: readonly Apt[]; sigungu: string | null }) {
  if (!data.length) return null;
  /* ㉑ §3-7 (R3) — 값이 없으면 null → 열 전체가 비면 열째 빼고, 일부만 비면 빈 칸 + sr-only 「해당 없음」. */
  const cellsOf = (a: Apt) => {
    const pp = fmt.manwon(a.avg_sale_price_pyeong);
    return {
      price: fmt.manwon(a.latest_sale_price),
      pp: pp ? `${pp}/평` : null,
      change: priceChangeCompact(a) || null,
      count: a.sale_count_1y > 0 ? `${fmtCnt(a.sale_count_1y)}건` : null,
    };
  };
  const cols = visibleColumns(['price', 'pp', 'change', 'count'] as const, data, cellsOf);
  const has = (k: 'price' | 'pp' | 'change' | 'count') => cols.includes(k);

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-title)', marginBottom: 10 }}>{sigungu ? `${sigungu} 주변 단지 비교` : '주변 단지 비교'}</div>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-xs)', minWidth: 400 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 'var(--fw-title)', color: 'var(--text-tertiary)', fontSize: 'var(--fs-xs)' }}>단지</th>
              {has('price') && <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'var(--fw-title)', color: 'var(--text-tertiary)', fontSize: 'var(--fs-xs)' }}>최근 매매</th>}
              {has('pp') && <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'var(--fw-title)', color: 'var(--text-tertiary)', fontSize: 'var(--fs-xs)' }}>평당가</th>}
              {has('change') && <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'var(--fw-title)', color: 'var(--text-tertiary)', fontSize: 'var(--fs-xs)' }}>변동률</th>}
              {has('count') && <th style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 'var(--fw-title)', color: 'var(--text-tertiary)', fontSize: 'var(--fs-xs)' }}>거래</th>}
            </tr>
          </thead>
          <tbody>
            {data.map((a, i) => { const c = cellsOf(a); return (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '8px', fontWeight: 'var(--fw-title)' }}>
                  {/* ⑲ⓒ K-5 — 링크 여부는 API 가 정한다(complex_href). 없으면 이름만. */}
                  <MaybeLink href={a.complex_href} style={{ color: 'var(--brand)', textDecoration: 'none' }}>{a.apt_name}</MaybeLink>
                  {a.built_year > 0 && <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-tertiary)', fontWeight: 'var(--fw-quiet)' }}>{a.built_year}년</div>}
                </td>
                {has('price') && <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'var(--fw-title)' }}>{cellOrNull(c.price)}</td>}
                {has('pp') && <td style={{ padding: '8px', textAlign: 'right', color: 'var(--text-secondary)' }}>{cellOrNull(c.pp)}</td>}
                {has('change') && <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'var(--fw-title)', color: priceChangeDirection(a) === 'up' ? 'var(--accent-red)' : priceChangeDirection(a) === 'down' ? 'var(--accent-blue)' : 'var(--text-tertiary)' }}>
                  {cellOrNull(c.change)}
                </td>}
                {has('count') && <td style={{ padding: '8px', textAlign: 'right', color: 'var(--text-tertiary)' }}>{cellOrNull(c.count)}</td>}
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
